// Agents list view. fn-1.29.
//
// **Acceptance**: "Agents list shows the user's accessible agents;
// click → 'Start run' creates a session and navigates to `/runs/:id`."
//
// **Wiring**:
//   - Source: `trpc.agents.list.useQuery()` (BFF proxy over `GET /agents`).
//   - Start-run: `trpc.runs.start.useMutation()` (BFF proxy over
//     `POST /sessions`). On success, navigate to `/runs/<id>`.
//   - `matterId` for `runs.start` is provided by a text input on each
//     row (mandatory at v1; the agent ALWAYS runs in a matter context).
//     A matter picker is a future enhancement; the input keeps the
//     surface dogfoodable without a matter directory.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useClientOtel, useTrackUserAction } from '../../instrumentation/provider.js';
import { trpc } from '../../trpc-client.js';

export function AgentsListPage(): React.ReactElement {
  const agentsQuery = trpc.agents.list.useQuery({});

  if (agentsQuery.isPending) {
    return (
      <section className="page page-agents" aria-busy="true">
        <h1>Agents</h1>
        <p>Loading agents…</p>
      </section>
    );
  }
  if (agentsQuery.error !== null) {
    return (
      <section className="page page-agents" role="alert">
        <h1>Agents</h1>
        <p>Failed to load agents: {agentsQuery.error.message}</p>
      </section>
    );
  }

  const agents = agentsQuery.data?.data ?? [];

  return (
    <section className="page page-agents">
      <h1>Agents</h1>
      <p style={{ color: 'var(--sb-text-muted)', margin: '0.25rem 0 1rem' }}>
        Create and manage autonomous agents. Click "Start session" to launch a new session against
        any visible agent.
      </p>
      {agents.length === 0 ? (
        <p style={{ color: 'var(--sb-text-muted)' }}>
          No agents available. Ask a firm admin to publish one.
        </p>
      ) : (
        <table className="sb-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Visibility</th>
              <th>ID</th>
              <th style={{ width: '22rem' }}>Start a session</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((agent) => (
              <AgentRow
                key={agent.id}
                agentId={agent.id}
                name={agent.name}
                visibility={agent.visibility}
              />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

interface AgentRowProps {
  readonly agentId: string;
  readonly name: string;
  readonly visibility: string;
}

function AgentRow(props: AgentRowProps): React.ReactElement {
  // Per-row matter id input. We keep state per row because users may
  // start multiple runs from the same page (different agents, different
  // matters).
  const [matterId, setMatterId] = useState('');
  // fn-2.2 — inline context document (no upload, no Files API mount).
  // Paste a CLAUDE.md / briefing here; the CP fires it as the bootstrap
  // user message before the operator's first message. The bootstrap
  // is visible in the thread for now (no hiding) — per dogfood
  // direction 2026-05-17, simpler than the prior file-mount + transcript-
  // pointer approach.
  const [contextDocument, setContextDocument] = useState('');
  const [contextOpen, setContextOpen] = useState(false);
  const navigate = useNavigate();
  const startMutation = trpc.runs.start.useMutation();
  const trackUserAction = useTrackUserAction();
  const otel = useClientOtel();

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    // Matter id is optional — when empty, the BFF synthesizes one so
    // the run is firm-level (no matter context).
    const normalizedMatterId = matterId.trim();
    // fn-1.31 — emit `spa.runs.start.click` before the mutation. The
    // spanId becomes the parent-id of the outbound tRPC traceparent so
    // the BFF's `bff.runs.start` span nests under this click. This
    // span lives on the USER trace (`hash(userId)`) — the platform
    // sessionId doesn't exist yet (it's the RESULT of the mutation).
    try {
      trackUserAction({
        name: 'spa.runs.start.click',
        metadata: {
          agent_id: props.agentId,
          ...(normalizedMatterId.length > 0 ? { matter_id: normalizedMatterId } : {}),
        },
      });
    } catch {
      // Telemetry is best-effort and must not block run creation.
    }
    try {
      const res = await startMutation.mutateAsync({
        agentId: props.agentId,
        ...(normalizedMatterId.length > 0 ? { matterId: normalizedMatterId } : {}),
        ...(contextDocument.trim().length > 0 ? { contextDocument } : {}),
      });
      // fn-1.31 round 8 — once the new platform sessionId exists,
      // prime its trace cache + emit a second span anchored on it.
      // This "linker" span lives on the SESSION trace
      // (`hash(res.id)`) and carries `user_click_span_id` in metadata
      // so a Langfuse reader can cross-walk from the user trace to
      // the session trace. Without this, the click is orphaned from
      // every subsequent session-scoped span (event/HITL/MCP).
      try {
        if (otel !== null) {
          await otel.primeSessionTrace(res.id);
        }
        trackUserAction({
          name: 'spa.runs.start.created',
          sessionId: res.id,
          metadata: {
            agent_id: props.agentId,
            ...(normalizedMatterId.length > 0 ? { matter_id: normalizedMatterId } : {}),
            session_id: res.id,
          },
        });
      } catch {
        // Telemetry is best-effort and must not block navigation.
      }
      navigate(`/runs/${encodeURIComponent(res.id)}`);
    } catch {
      // The mutation surfaces error state via `startMutation.error`;
      // we render it below.
    }
  };

  return (
    <tr>
      <td style={{ fontWeight: 500 }}>{props.name}</td>
      <td>
        <span
          className="sb-chip"
          style={{ textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.05em' }}
        >
          {props.visibility}
        </span>
      </td>
      <td className="sb-cell-id" title={props.agentId}>
        {props.agentId.slice(0, 12)}…
      </td>
      <td>
        <form className="agents-list-row-form" onSubmit={submit}>
          <label className="visually-hidden" htmlFor={`matter-${props.agentId}`}>
            Matter id
          </label>
          <input
            id={`matter-${props.agentId}`}
            className="agents-list-row-input"
            type="text"
            placeholder="Matter id (optional)"
            value={matterId}
            onChange={(e) => setMatterId(e.target.value)}
            disabled={startMutation.isPending}
          />
          <button
            type="submit"
            disabled={startMutation.isPending}
            className="agents-list-row-start"
          >
            {startMutation.isPending ? 'Starting…' : 'Start session'}
          </button>
        </form>
        {/* fn-2.2 — inline context document. Disclosure-style so the
            Agents row stays compact when not in use. Paste a CLAUDE.md
            here; the CP fires it as the bootstrap user message
            (visible in the thread). No upload, no Files page mount. */}
        <div className="agents-list-row-context" style={{ marginTop: '0.5rem' }}>
          <button
            type="button"
            className="agents-list-row-context-toggle"
            onClick={() => setContextOpen((v) => !v)}
            style={{
              appearance: 'none',
              background: 'transparent',
              border: 'none',
              color: 'var(--sb-text-muted)',
              fontSize: '0.72rem',
              cursor: 'pointer',
              padding: 0,
              textAlign: 'left',
            }}
          >
            {contextOpen ? '▾' : '▸'} Paste context (sent as the first thread message)
            {contextDocument.trim().length > 0 && !contextOpen && (
              <span style={{ marginLeft: '0.4rem', color: 'var(--sb-status-completed)' }}>
                · {contextDocument.length} chars
              </span>
            )}
          </button>
          <label className="visually-hidden" htmlFor={`context-${props.agentId}`}>
            Context document for new session
          </label>
          {contextOpen && (
            <textarea
              id={`context-${props.agentId}`}
              className="agents-list-row-context-input"
              value={contextDocument}
              onChange={(e) => setContextDocument(e.target.value)}
              placeholder="Paste a CLAUDE.md / briefing here. The agent sees this as the first message; you'll see it in the thread too."
              rows={4}
              disabled={startMutation.isPending}
              style={{
                marginTop: '0.35rem',
                width: '100%',
                minWidth: '20rem',
                padding: '0.5rem 0.65rem',
                border: '1px solid var(--sb-border)',
                borderRadius: '6px',
                background: 'var(--sb-bg-panel)',
                fontFamily: 'inherit',
                fontSize: '0.82rem',
                resize: 'vertical',
              }}
            />
          )}
        </div>
        {startMutation.error !== null && (
          <p
            className="agents-list-row-error"
            role="alert"
            style={{ color: 'var(--sb-status-failed)', margin: '0.35rem 0 0', fontSize: '0.82rem' }}
          >
            {startMutation.error.message}
          </p>
        )}
      </td>
    </tr>
  );
}
